import { transformUrnToWitboostId } from '@agilelab/plugin-wb-builder-common';
import {
  Pagination,
  RoleSettingsView,
  RoleViewFilters,
} from '@agilelab/plugin-wb-rbac-common';
import { parseEntityRef } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import useAsync from 'react-use/lib/useAsync';
import { getEntitiesDisplayNames } from './enrichDetails';

export interface RoleContextType {
  filters: RoleViewFilters;
  changeFilters: <K extends keyof RoleViewFilters>(
    key: K,
    value: RoleViewFilters[K],
  ) => void;
  pagination: Pagination;
  setPagination: (p: Pagination) => void;
  roles: RoleSettingsView[];
  loading: boolean;
  roleCount: number;
  reloadTable: () => void;
}

export const RoleContext = createContext<RoleContextType>(
  {} as RoleContextType,
);

type Props = {
  children: ReactNode;
};

export const RoleContextProvider: FC<Props> = ({ children }) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [loading, setLoading] = useState(true);
  const [rolesTotal, setRolesTotal] = useState<number>(0);

  const [roles, setRoles] = useState<RoleSettingsView[]>([]);

  const [filters, setFilters] = useState<RoleViewFilters>({
    text: '',
    limit: 25,
  });
  const [pagination, setPagination] = useState<Pagination>({
    limit: filters.limit ?? 25,
    offset: 0,
  });

  useAsync(async () => {
    const response = await fetchApi.fetch(
      `${await discoveryApi.getBaseUrl('rbac')}/rolesandsubjects?offset=${
        pagination.offset
      }&limit=${pagination.limit}&searchKeyword=${filters.text}`,
    );

    const data = await response.json();
    const enrichedRoles = data.roles.map((role: any) => {
      const witboostId = transformUrnToWitboostId(role.entity_ref ?? '');
      const entityRef = witboostId ? parseEntityRef(witboostId) : undefined;
      return { transformed_entity_ref: entityRef?.name ?? '', ...role };
    });

    const entitiesFilters: { 'metadata.name': string }[] = (
      [
        ...new Set(
          enrichedRoles.map(
            (role: RoleSettingsView) => role.transformed_entity_ref,
          ),
        ),
      ] as string[]
    ).map((entityRef: string) => ({
      'metadata.name': entityRef,
    }));

    // holds a map of entity name and its display name
    const displayNamesMap = await getEntitiesDisplayNames(
      catalogApi,
      entitiesFilters,
      {
        chunkLimit: 50,
      },
    );

    const userFriendlyRoles = enrichedRoles.flatMap(
      (role: RoleSettingsView) => {
        return displayNamesMap.has(role.transformed_entity_ref.toLowerCase())
          ? [
              {
                ...role,
                entity_ref_displayname:
                  displayNamesMap.get(role.transformed_entity_ref) ??
                  role.transformed_entity_ref,
              } as RoleSettingsView,
            ]
          : [
              {
                ...role,
                entity_ref_displayname: role.transformed_entity_ref,
                warning: role.transformed_entity_ref !== '',
              } as RoleSettingsView,
            ];
      },
    );

    setRoles(userFriendlyRoles);
    setRolesTotal((data.total as number) ?? 0);
    setLoading(false);
  }, [
    catalogApi,
    identityApi,
    discoveryApi,
    pagination.offset,
    pagination.limit,
    filters.text,
  ]);

  const reloadTable = useCallback(() => {
    setPagination({ ...pagination, offset: 0 });
  }, [setPagination, pagination]);

  const contextValue = useMemo(
    () => ({
      filters,
      changeFilters: <K extends keyof RoleViewFilters>(
        key: K,
        filterValue: RoleViewFilters[K],
      ) => {
        setPagination(p => ({ ...p, offset: 0 }));
        setFilters(f => ({ ...f, [key]: filterValue }));
      },
      pagination,
      setPagination,
      roles,
      roleCount: rolesTotal,
      reloadTable,
      loading,
    }),
    [filters, pagination, roles, rolesTotal, reloadTable, loading],
  );

  return (
    <RoleContext.Provider value={contextValue}>{children}</RoleContext.Provider>
  );
};

export const useRoleContext = () => useContext(RoleContext);
